import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  LogOut,
  ChefHat,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  Utensils,
  ShoppingCart,
  Star,
  DollarSign,
  Package,
  Thermometer,
  Brain,
  Target,
  Activity
} from 'lucide-react'
import InfoTooltip from '../common/InfoTooltip'

export default function RestauranteDashboard({ onBack, onLogout, businessData }) {
  const kpis = [
    {
      title: 'Margen de Ganancia',
      value: '28%',
      change: '+12%',
      trend: 'up',
      icon: DollarSign,
      color: 'green',
      tooltip: {
        title: 'Optimización de Margen de Ganancia',
        description: 'Incremento en el margen de ganancia mediante optimización inteligente de precios, control de costos y análisis de rentabilidad.',
        aiMethod: 'Análisis de costos por platillo, optimización dinámica de precios, predicción de demanda, análisis de rentabilidad.',
        dataSource: 'Costos de ingredientes, precios de competencia, datos de ventas, análisis de márgenes históricos.',
        updateFrequency: 'Optimización diaria con análisis semanal de tendencias'
      }
    },
    {
      title: 'Reducción Desperdicio',
      value: '40%',
      change: '+15%',
      trend: 'up',
      icon: Package,
      color: 'blue',
      description: 'Menor desperdicio',
      tooltip: {
        title: 'Reducción Inteligente de Desperdicio',
        description: 'Disminución del desperdicio de alimentos mediante predicción de demanda y gestión optimizada de inventario.',
        aiMethod: 'Predicción de demanda por platillo, análisis de patrones estacionales, optimización de compras, alertas de vencimiento.',
        dataSource: 'Histórico de ventas, datos de inventario, patrones de demanda, fechas de caducidad.',
        updateFrequency: 'Predicciones diarias con ajustes en tiempo real'
      }
    },
    {
      title: 'Satisfacción Cliente',
      value: '94%',
      change: '+6%',
      trend: 'up',
      icon: Star,
      color: 'orange',
      tooltip: {
        title: 'Índice de Satisfacción del Cliente',
        description: 'Medición de satisfacción basada en reviews, tiempo de servicio y calidad percibida, optimizada con IA.',
        aiMethod: 'Análisis de sentimientos en reviews, monitoreo de tiempos de servicio, predicción de satisfacción.',
        dataSource: 'Reviews online, encuestas post-comida, tiempos de servicio, feedback directo.',
        updateFrequency: 'Análisis continuo con consolidación diaria'
      }
    },
    {
      title: 'Eficiencia Cocina',
      value: '87%',
      change: '+18%',
      trend: 'up',
      icon: ChefHat,
      color: 'purple',
      tooltip: {
        title: 'Optimización de Eficiencia en Cocina',
        description: 'Mejora en los tiempos de preparación y optimización de procesos culinarios mediante análisis de flujo de trabajo.',
        aiMethod: 'Análisis de tiempos de preparación, optimización de secuencias de cocina, predicción de picos de demanda.',
        dataSource: 'Tiempos de preparación por platillo, órdenes por hora, análisis de flujo de cocina.',
        updateFrequency: 'Monitoreo en tiempo real con optimización diaria'
      }
    }
  ]

  const menuOptimization = [
    {
      dish: 'Pasta Carbonara',
      popularity: 89,
      profitability: 76,
      aiRecommendation: 'Aumentar precio 8% - demanda inelástica',
      orders: 156,
      revenue: '+23%',
      color: 'green'
    },
    {
      dish: 'Salmón Teriyaki',
      popularity: 67,
      profitability: 94,
      aiRecommendation: 'Promocionar más - alta rentabilidad',
      orders: 89,
      revenue: '+45%',
      color: 'blue'
    },
    {
      dish: 'Pizza Margherita',
      popularity: 95,
      profitability: 58,
      aiRecommendation: 'Optimizar costos - ingredientes premium',
      orders: 234,
      revenue: '+12%',
      color: 'orange'
    },
    {
      dish: 'Ensalada César',
      popularity: 45,
      profitability: 82,
      aiRecommendation: 'Remover del menú - baja rotación',
      orders: 34,
      revenue: '-8%',
      color: 'red'
    }
  ]

  const inventoryInsights = [
    {
      category: 'Proteínas',
      stock: 78,
      forecast: 'Reabastecer en 3 días',
      wasteReduction: 35,
      savings: '$2,340',
      status: 'optimal'
    },
    {
      category: 'Vegetales',
      stock: 92,
      forecast: 'Stock suficiente por 5 días',
      wasteReduction: 42,
      savings: '$1,890',
      status: 'optimal'
    },
    {
      category: 'Lácteos',
      stock: 23,
      forecast: 'Crítico - reabastecer hoy',
      wasteReduction: 28,
      savings: '$980',
      status: 'critical'
    },
    {
      category: 'Condimentos',
      stock: 156,
      forecast: 'Sobrestock - usar primero',
      wasteReduction: 18,
      savings: '$450',
      status: 'excess'
    }
  ]

  const customerAnalytics = [
    {
      segment: 'Familias',
      percentage: 35,
      avgSpend: '$580',
      preferences: ['Pizza', 'Pasta', 'Infantiles'],
      visitFreq: '2.3x/mes',
      satisfaction: 92
    },
    {
      segment: 'Parejas',
      percentage: 28,
      avgSpend: '$720',
      preferences: ['Mariscos', 'Vinos', 'Postres'],
      visitFreq: '1.8x/mes',
      satisfaction: 96
    },
    {
      segment: 'Ejecutivos',
      percentage: 22,
      avgSpend: '$450',
      preferences: ['Ensaladas', 'Rápido', 'Saludable'],
      visitFreq: '3.1x/mes',
      satisfaction: 89
    },
    {
      segment: 'Jóvenes',
      percentage: 15,
      avgSpend: '$320',
      preferences: ['Hamburguesas', 'Bebidas', 'Compartir'],
      visitFreq: '2.7x/mes',
      satisfaction: 94
    }
  ]

  const aiRecommendations = [
    {
      type: 'menu',
      message: 'Agregar "Bowl de Quinoa" - tendencia saludable detectada',
      impact: '+15% ventas estimadas',
      confidence: 89
    },
    {
      type: 'pricing',
      message: 'Reducir precio pasta 5% incrementará volumen 22%',
      impact: '+$3,200 ingresos/mes',
      confidence: 94
    },
    {
      type: 'inventory',
      message: 'Cambiar proveedor de vegetales - mejor calidad/precio',
      impact: '-12% costos ingredientes',
      confidence: 87
    }
  ]

  const peakHours = [
    { hour: '12:00', orders: 45, waitTime: '8 min', efficiency: 92 },
    { hour: '13:00', orders: 67, waitTime: '12 min', efficiency: 85 },
    { hour: '14:00', orders: 89, waitTime: '15 min', efficiency: 78 },
    { hour: '19:00', orders: 78, waitTime: '10 min', efficiency: 88 },
    { hour: '20:00', orders: 95, waitTime: '18 min', efficiency: 72 },
    { hour: '21:00', orders: 56, waitTime: '14 min', efficiency: 83 }
  ]

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-red-100 text-red-800'
    }
    return colors[color] || colors.blue
  }

  const getIconColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    }
    return colors[color] || colors.blue
  }

  const getStockStatus = (status) => {
    const statuses = {
      optimal: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      critical: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle },
      excess: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Package }
    }
    return statuses[status] || statuses.optimal
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="flex items-center">
                <div className={`w-8 h-8 bg-gradient-to-br ${businessData.color} rounded-lg flex items-center justify-center mr-3`}>
                  <ChefHat className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{businessData.title}</h1>
                  <p className="text-sm text-gray-500">Food Analytics con IA</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPIs Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {kpis.map((kpi, index) => {
            const IconComponent = kpi.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{kpi.title}</p>
                      <InfoTooltip 
                        title={kpi.tooltip.title}
                        description={kpi.tooltip.description}
                        aiMethod={kpi.tooltip.aiMethod}
                        dataSource={kpi.tooltip.dataSource}
                        updateFrequency={kpi.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {kpi.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        {kpi.description || 'vs mes anterior'}
                      </span>
                    </div>
                  </div>
                  <div className={`p-3 ${getIconColorClasses(kpi.color)} rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Optimización de Menú */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">Optimización de Menú IA</h3>
                  <InfoTooltip 
                    title="Motor de Optimización de Menú"
                    description="Sistema inteligente que analiza la popularidad y rentabilidad de cada platillo para optimizar el menú del restaurante."
                    aiMethod="Análisis de popularidad vs rentabilidad, predicción de demanda por platillo, optimización de precios."
                    dataSource="Datos de ventas por platillo, costos de ingredientes, feedback de clientes, análisis de competencia."
                    updateFrequency="Análisis diario con recomendaciones semanales"
                    iconColor="text-gray-400"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-orange-600" />
                  <span className="text-sm text-gray-600">Food Engine</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {menuOptimization.map((dish, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{dish.dish}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getColorClasses(dish.color)}`}>
                        {dish.orders} órdenes
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-gray-600">Popularidad</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-blue-500"
                            style={{ width: `${dish.popularity}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{dish.popularity}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Rentabilidad</p>
                        <div className="mt-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{ width: `${dish.profitability}%` }}
                          />
                        </div>
                        <p className="text-sm font-medium mt-1">{dish.profitability}%</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Ingresos</p>
                        <p className={`text-lg font-bold ${dish.revenue.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                          {dish.revenue}
                        </p>
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-3">
                      <p className="text-sm text-orange-800">
                        <strong>Recomendación IA:</strong> {dish.aiRecommendation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Análisis por Horas Pico */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Eficiencia en Horas Pico
                </h3>
                <InfoTooltip 
                  title="Análisis de Eficiencia en Horas Pico"
                  description="Monitoreo y optimización del rendimiento del restaurante durante las horas de mayor demanda."
                  aiMethod="Análisis de flujo de trabajo, optimización de asignación de personal, predicción de carga de trabajo."
                  dataSource="Datos de órdenes por hora, tiempos de preparación, eficiencia del personal, patrones de demanda."
                  updateFrequency="Monitoreo en tiempo real con análisis diario"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {peakHours.map((hour, index) => (
                  <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-16 text-sm font-medium text-gray-700">{hour.hour}</div>
                    
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Órdenes</p>
                        <p className="text-lg font-bold text-gray-900">{hour.orders}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Tiempo Espera</p>
                        <p className="text-lg font-bold text-orange-600">{hour.waitTime}</p>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Eficiencia</p>
                        <p className={`text-lg font-bold ${hour.efficiency >= 85 ? 'text-green-600' : hour.efficiency >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {hour.efficiency}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Gestión de Inventario */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Inventario Inteligente</h3>
                <InfoTooltip 
                  title="Sistema de Gestión Inteligente de Inventario"
                  description="Predicción automática de necesidades de inventario y optimización de compras para minimizar desperdicio y garantizar disponibilidad."
                  aiMethod="Predicción de demanda por ingrediente, análisis de patrones estacionales, optimización de pedidos automáticos."
                  dataSource="Consumo histórico de ingredientes, ventas por platillo, fechas de caducidad, proveedores."
                  updateFrequency="Predicciones diarias con alertas automáticas de restock"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {inventoryInsights.map((item, index) => {
                  const statusConfig = getStockStatus(item.status)
                  const StatusIcon = statusConfig.icon
                  
                  return (
                    <div key={index} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{item.category}</h4>
                        <div className={`p-1 rounded-full ${statusConfig.bg}`}>
                          <StatusIcon className={`w-4 h-4 ${statusConfig.text.replace('text-', 'text-').replace('-800', '-600')}`} />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Stock:</span>
                          <span className="text-sm font-medium">{item.stock}%</span>
                        </div>
                        
                        <div className="bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              item.stock >= 70 ? 'bg-green-500' :
                              item.stock >= 30 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.stock}%` }}
                          />
                        </div>
                        
                        <p className="text-xs text-gray-600 mt-2">{item.forecast}</p>
                        
                        <div className="bg-gray-50 rounded p-2 mt-3">
                          <p className="text-xs text-gray-700">
                            <strong>Ahorro desperdicio:</strong> -{item.wasteReduction}% ({item.savings})
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>

            {/* Segmentación de Clientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Segmentos de Cliente</h3>
                <InfoTooltip 
                  title="Análisis de Segmentación de Clientes"
                  description="Segmentación automática de clientes basada en patrones de consumo, frecuencia y preferencias para personalizar estrategias."
                  aiMethod="Clustering de clientes por comportamiento, análisis de valor de por vida del cliente, personalización de ofertas."
                  dataSource="Historial de compras, frecuencia de visitas, preferencias de platillos, datos demográficos."
                  updateFrequency="Resegmentación mensual con análisis continuo"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-4">
                {customerAnalytics.map((segment, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{segment.segment}</h4>
                      <span className="text-sm font-bold text-gray-700">{segment.percentage}%</span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gasto promedio:</span>
                        <span className="font-medium">{segment.avgSpend}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Frecuencia:</span>
                        <span className="font-medium">{segment.visitFreq}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Satisfacción:</span>
                        <span className="font-medium">{segment.satisfaction}%</span>
                      </div>
                      
                      <div className="mt-2">
                        <p className="text-gray-600 text-xs">Preferencias:</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {segment.preferences.map((pref, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                              {pref}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Recomendaciones IA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border p-6"
            >
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Recomendaciones IA</h3>
                <InfoTooltip 
                  title="Motor de Recomendaciones Inteligentes"
                  description="Sistema que genera recomendaciones automáticas para optimizar operaciones, menú, marketing y rentabilidad del restaurante."
                  aiMethod="Machine learning para análisis predictivo, optimización multi-objetivo, recomendaciones basadas en datos históricos."
                  dataSource="Datos de ventas, inventario, feedback de clientes, análisis de competencia, tendencias del mercado."
                  updateFrequency="Recomendaciones actualizadas diariamente con nivel de confianza"
                  iconColor="text-gray-400"
                />
              </div>
              
              <div className="space-y-3">
                {aiRecommendations.map((rec, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg p-3">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-orange-100 rounded-full flex-shrink-0">
                        <Brain className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{rec.message}</p>
                        <p className="text-xs text-green-600 font-medium mt-1">{rec.impact}</p>
                        <p className="text-xs text-gray-500 mt-1">Confianza: {rec.confidence}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
} 